'use client';

import { useEffect, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@heidi/design-system/button';
import {
  Spotlight,
  SpotlightAnchor,
  SpotlightArrow,
  SpotlightClose,
  SpotlightContent,
  SpotlightDescription,
  SpotlightFooter,
  SpotlightHeader,
  SpotlightTitle,
} from '@heidi/design-system/spotlight';
import { TypographyCaption } from '@heidi/design-system/typography';
import { isTauri } from '@heidi/runtime/platform';
import { ModalId, SignupSource } from '@heidi/schema/ml-scribe';
import { useQueryUser } from '@heidi/workspace/data';

import { getDictationAnalyticsPorts } from '../../../../dictation-analytics-ports';
import { getDictationModalQueuePorts } from '../../../../dictation-modal-queue-ports';
import {
  hasTrackedView,
  markViewAsTracked,
} from './dictate-scribe-cross-sell-spotlight.utils';

interface DictateScribeCrossSellSpotlightProps {
  children: ReactNode;
  /** Whether the app router is on the Dictate history route. */
  isOnDictationLogsRoute: boolean;
  /** The app's sidebar session creator (`useNewSession(...).createNewSession`). */
  onStartScribeSession: (tracking: {
    metadata: { entryPoint: 'dictate_cross_sell' };
  }) => void;
}

export function DictateScribeCrossSellSpotlight({
  children,
  isOnDictationLogsRoute,
  onStartScribeSession,
}: DictateScribeCrossSellSpotlightProps) {
  const intl = useIntl();
  const { isOpen, handleClose } = getDictationModalQueuePorts().useQueuedModal(
    ModalId.SCRIBE_CROSS_SELL_SPOTLIGHT
  );
  const { data: signupSource } = useQueryUser((data) => data?.signup_source);

  // Belt-and-braces: launchModalsAtom already drops this off `/dictation-logs`.
  const isVisible =
    isOpen &&
    isTauri() &&
    signupSource === SignupSource.dictate &&
    isOnDictationLogsRoute;

  useEffect(() => {
    if (!isVisible || hasTrackedView()) return;
    markViewAsTracked();
    getDictationAnalyticsPorts().logs.trackCrossSellViewed();
  }, [isVisible]);

  if (!isVisible) return <>{children}</>;

  const handleTryItOut = () => {
    onStartScribeSession({ metadata: { entryPoint: 'dictate_cross_sell' } });
    handleClose();
  };

  // No onOpenChange: Radix fires `false` on sidebar-mode swaps, falsely marking seen.
  return (
    <Spotlight open>
      <SpotlightAnchor>{children}</SpotlightAnchor>
      <SpotlightContent
        align="start"
        alignOffset={0}
        side="right"
        sideOffset={12}
        updatePositionStrategy="always"
        onEscapeKeyDown={handleClose}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
        data-testid="scribe-cross-sell-spotlight"
      >
        <SpotlightArrow width={12} height={8} />
        <SpotlightHeader>
          <SpotlightTitle>
            <FormattedMessage
              id="QtJqNvd7UC"
              defaultMessage="Transcribe patient conversations"
            />
          </SpotlightTitle>
          <SpotlightClose
            onClick={handleClose}
            aria-label={intl.formatMessage({ id: 'rbrahOGMC3', defaultMessage: 'Close' })}
          />
        </SpotlightHeader>
        <SpotlightDescription>
          <TypographyCaption className="text-text-primary-invert">
            <FormattedMessage
              id="RhNnXXNbaA"
              defaultMessage="Be more present. Use Scribe to ambiently capture and structure documentation during your next consult."
            />
          </TypographyCaption>
        </SpotlightDescription>
        <SpotlightFooter className="justify-end">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleTryItOut}
            className="bg-surface text-text-primary hover:bg-background-secondary"
          >
            <FormattedMessage id="wZ6Rtf7Xp5" defaultMessage="Try it out" />
          </Button>
        </SpotlightFooter>
      </SpotlightContent>
    </Spotlight>
  );
}
